import { forwardRef, type HTMLAttributes } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";

// ════════════════════════════════════════════════════════════════════════════
// GlassCard Component
// ════════════════════════════════════════════════════════════════════════════
// Glassmorphism card with frosted glass effect.
// Uses CSS classes from globals.css for consistent styling.

type GlassCardVariant = "default" | "elevated" | "surface";

interface GlassCardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: GlassCardVariant;
  animated?: boolean;
}

const variantClasses: Record<GlassCardVariant, string> = {
  default: "glass-card",
  elevated: "glass-card-elevated",
  surface: "glass-surface",
};

export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
  ({ variant = "default", animated = false, className = "", children, ...props }, ref) => {
    const baseClass = variantClasses[variant];
    const classes = `${baseClass} ${className}`.trim();

    if (animated) {
      return (
        <motion.div
          ref={ref}
          className={classes}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          {...(props as HTMLMotionProps<"div">)}
        >
          {children}
        </motion.div>
      );
    }

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

GlassCard.displayName = "GlassCard";

export default GlassCard;
